"use client"

import { PriceQuote } from "@/app/offers/price-quote"
import { useOfferValidation } from "@/app/offers/use-offer-validation"
import { PriceInput } from "@/app/price/price-input"
import { useSolanaItemOffer } from "@/app/sdk/action-molecules/solana/solana-item-offer-molecule"
import type { Nft } from "@anaconda/core/nft/types"
import { Trans } from "@lingui/react/macro"
import {
  Button,
  DialogHeader,
  DialogTitle,
} from "@rarible_int/anaconda_ui_components"
import { atom } from "@reatom/core"
import { reatomComponent } from "@reatom/react"
import { useMemo } from "react"
import { useNativeCurrency } from "../../blockchains/use-blockchain"
import { ItemPreview } from "./item-preview"

interface Props {
  nft: Nft
  onOpenChange: (isOpened: boolean) => void
}

// Item offers are always for a single item: the on-chain bid is created with
// size = 1 and there is no quantity picker.
const QUANTITY = 1

export const SolanaItemOfferDialogContent = reatomComponent<Props>(
  ({ nft, onOpenChange }) => {
    const currency = useNativeCurrency(nft.collection.blockchain)
    const { mutate } = useSolanaItemOffer(nft)

    const { priceAtom } = useMemo(() => {
      const priceAtom = atom<number | undefined>(
        undefined,
        "solanaItemOfferPrice",
      )

      return { priceAtom }
    }, [])

    const price = priceAtom()
    const setPrice = (value: number | undefined) => priceAtom.set(value)
    // No collection floor here — the testnet "within 75% of floor" rule only
    // applies to collection offers.
    const { error } = useOfferValidation({
      price,
      quantity: QUANTITY,
      currency,
    })

    return (
      <form
        className="flex w-full min-w-0 flex-col gap-y-2"
        onSubmit={(e) => {
          e.preventDefault()
          if (price === undefined) return
          mutate({ price, currency: currency.id })
          onOpenChange(false)
        }}
      >
        <DialogHeader className="pb-2">
          <DialogTitle className="text-center text-lg">
            <Trans>Make offer</Trans>
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <ItemPreview nft={nft} />
          <PriceInput
            onPriceChange={setPrice}
            error={error}
            currency={currency}
          />
          <PriceQuote
            quantity={QUANTITY}
            price={{ amount: price?.toString() ?? "0", currency }}
          />
          <p className="px-1 text-foreground/60 text-xs">
            <Trans>
              Your {currency.abbreviation} is escrowed on-chain until the owner
              accepts this offer or you cancel it. Offers do not expire.
            </Trans>
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            className="flex-grow"
            variant="secondary"
            type="button"
            onClick={() => onOpenChange(false)}
          >
            <Trans>Cancel</Trans>
          </Button>
          <Button
            className="flex-grow"
            type="submit"
            disabled={!price || !!error}
          >
            <Trans>Make offer</Trans>
          </Button>
        </div>
      </form>
    )
  },
  "SolanaItemOfferDialogContent",
)
